import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { get, patch, post } from 'helpers/api_helper'
import { store as socketStore } from 'components/Common/Socket/store'
import { newPlayerOperationId } from 'pages/UserAccounts/PlayerAccounts/playerInvitationState'
import { competitionOperationMessage, runCompetitionOperation } from './competitionOperation'

export default function useCompetitionOperation({ id, userId, onConfirmed, storageScope = "" }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const key = 'competition-management:' + userId + ':' + id + (storageScope ? ':' + storageScope : '')
  const active = useRef(null), changed = useRef(onConfirmed)
  const [state, setState] = useState({})
  const [cleanups, setCleanups] = useState([])
  const cleanupRecords = useRef([]), cleanupRunners = useRef(new Set())
  const cleanupKey = key + ':cleanup'
  const retainCleanup = useCallback((operation, result) => {
    const remaining = cleanupRecords.current.filter(item => item.operation.operationId !== operation.operationId)
    if (result.cleanupStatus && result.cleanupStatus !== 'completed') remaining.push({ operation, ...result })
    if (remaining.length) sessionStorage.setItem(cleanupKey, JSON.stringify(remaining.map(item => item.operation)))
    else sessionStorage.removeItem(cleanupKey)
    cleanupRecords.current = remaining
    setCleanups(remaining)
  }, [cleanupKey])
  changed.current = onConfirmed
  const observe = useCallback((operation, submit, firstSubmission = false) => {
    active.current?.runner.cancel()
    const runner = runCompetitionOperation({ operation, socket, submit, firstSubmission, read: operationId => get('/admin/competition-operations/' + operationId) })
    const request = { runner, operation }
    active.current = request
    setState({ status: 'pending', operation })
    runner.promise.then(result => {
      if (active.current !== request || result.status === 'cancelled') return
      if (['saved', 'error'].includes(result.status)) {
        const cleanupPending = result.cleanupStatus && result.cleanupStatus !== 'completed'
        try {
          retainCleanup(operation, result)
          sessionStorage.removeItem(key)
          active.current = null
        } catch { /* Keep the active identifier if a separate cleanup record cannot be stored. */ }
        setState({ ...result, operation, cleanupPending, message: result.status === 'saved' ? (result.action === 'pdf' && result.competitionDetailsPdf === null ? 'PDF removed.' : 'Competition updated.') : result.message })
        changed.current?.(result)
      } else setState({ ...result, operation })
    })
  }, [key, socket, retainCleanup])
  useEffect(() => {
    setState({}); setCleanups([]); cleanupRecords.current = []
    let mounted = true
    const runners = cleanupRunners.current
    if (userId) {
      try {
        const unfinished = JSON.parse(sessionStorage.getItem(cleanupKey) || '[]')
        if (Array.isArray(unfinished)) {
          cleanupRecords.current = unfinished.filter(op => op?.eventId === id && op.operationId && op.competitionId && op.correlationId).map(operation => ({ operation, cleanupStatus: 'pending' }))
          setCleanups(cleanupRecords.current)
          for (const { operation } of cleanupRecords.current) get('/admin/competition-operations/' + operation.operationId).then(response => {
            if (mounted && response.data?.operationId === operation.operationId && ['saved', 'error'].includes(response.data.status)) retainCleanup(operation, { ...response.data, message: competitionOperationMessage(response.data.message) })
          }).catch(() => { /* Keep the identifier visible for manual recovery. */ })
        }
        const saved = JSON.parse(sessionStorage.getItem(key) || 'null')
        if (saved?.eventId === id && saved.operationId && saved.competitionId && saved.correlationId) observe(saved)
      } catch { /* A malformed record never submits a mutation. */ }
    }
    return () => { mounted = false; active.current?.runner.cancel(); active.current = null; runners.forEach(runner => runner.cancel()); runners.clear() }
  }, [id, key, observe, userId, cleanupKey, retainCleanup])
  // `texts` (P92, Tenis10): the PDF's title and description, sent with its file; 'pdfTexts' sends them alone, with the current file.
  const run = async (competition, action, file, texts) => {
    if (active.current || !userId) return
    const removePdf = action === 'removePdf'
    const textsOnly = action === 'pdfTexts'
    if (removePdf || textsOnly) action = 'pdf'
    const operation = { eventId: id, competitionId: competition._id, action, operationId: newPlayerOperationId(), correlationId: newPlayerOperationId() }
    try { sessionStorage.setItem(key, JSON.stringify(operation)) } catch {
      setState({ status: 'error', message: 'Browser session storage is unavailable. Enable it before saving so the result can be recovered.' }); return
    }
    // A PDF upload is the one command sent in two requests: its upload stages a pending operation that only Recover closes, so an
    // error after staging is marked `staged` and stays open to recovery (Events ADR 0004). Every other 4xx of a first send is final.
    observe(operation, async () => {
      const config = { headers: { 'X-Correlation-Id': operation.correlationId } }
      let filename = textsOnly ? competition.competitionDetailsPdf : null
      if (action === 'pdf' && !removePdf && !textsOnly) {
        const form = new FormData()
        form.append('file', file)
        let upload
        try {
          upload = await post('/assets/events/' + id, form, { headers: { ...config.headers, 'X-Operation-Id': operation.operationId,
            'X-Competition-Id': competition._id, 'X-Competition-Revision': competition.revision } })
        } catch (error) {
          const response = error?.response
          // The gateway stores the file after staging, then finds the operation already closed by Recover.
          if (response?.status === 409 && response.data?.message === 'COMPETITION_OPERATION_NOT_RECEIVED') error.staged = true
          // A bad file name is refused before staging. Renamed in place, the error keeps its 400 and is a refusal with its own words.
          else if (response?.status === 400 && response.data?.message === 'INVALID_COMPETITION_PDF') response.data = { ...response.data, message: 'INVALID_COMPETITION_PDF_NAME' }
          throw error
        }
        filename = upload?.data?.filename
        if (!filename) throw new Error('PDF upload was not confirmed.')
      }
      const path = ['wildCards-plus', 'wildCards-minus'].includes(action) ? '/wildCards/' + action.split('-')[1] : action === 'pdf' ? '/pdf' : '/status/' + action
      try {
        return await patch('/admin/events/' + id + '/competitions/' + competition._id + path,
          { operationId: operation.operationId, revision: competition.revision, socketClientId, ...(action === 'pdf' ? { filename, ...(texts || {}) } : {}) }, config)
      } catch (error) {
        // Remove PDF and a change of the texts alone send no file: only an association after an upload follows a staged operation.
        if (filename && !textsOnly) error.staged = true
        throw error
      }
    }, true)
  }
  const recover = () => {
    const operation = active.current?.operation || state.operation
    if (operation) observe(operation, () => post('/admin/competition-operations/' + operation.operationId + '/retry',
      { socketClientId }, { headers: { 'X-Correlation-Id': operation.correlationId } }))
  }
  const recoverCleanup = operation => {
    const runner = runCompetitionOperation({ operation, socket,
      read: operationId => get('/admin/competition-operations/' + operationId),
      submit: () => post('/admin/competition-operations/' + operation.operationId + '/retry', { socketClientId }, { headers: { 'X-Correlation-Id': operation.correlationId } }) })
    cleanupRunners.current.add(runner)
    runner.promise.then(result => {
      cleanupRunners.current.delete(runner)
      if (['saved', 'error'].includes(result.status)) {
        try { retainCleanup(operation, result) } catch { /* Preserve the prior warning if storage is unavailable. */ }
      }
    })
  }
  return { ...state, cleanups, pending: ['pending', 'unknown'].includes(state.status), run, recover, recoverCleanup }
}
